import { Button, Col, Form, Input, Radio, Row, Select, TimePicker, Tooltip, Upload } from 'antd';
import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { ReactSVG } from 'react-svg';
import ReactMapGL from 'react-map-gl';
import { DeleteOutlined, InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as toGeoJSON from '@mapbox/togeojson';

import './DeliveryAreaForm.scss';
import DrawControl from './DrawPolygonControl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';
import PostalCodeInput from '../../Elements/PostalCodesInput';
import moment from 'moment';

// function polygonsToGeoJSON(area) {
//   const features = area?.area?.coordinates.map((coordinates, index) => {
//     return {
//       type: 'Feature',
//       properties: {
//         id: index, // Assign a unique ID or pre-loaded areas
//       },
//       geometry: {
//         type: 'Polygon',
//         coordinates,
//       },
//     };
//   });

//   return {
//     type: 'FeatureCollection',
//     features: features || [],
//   };
// }

// const kmlPolygonsToFeatureCollection = (polygon) => {
//   const features = Object.entries(polygon).map(([id, coordinates]) => ({
//     type: 'Feature',
//     properties: { id: parseInt(id) },
//     geometry: {
//       type: 'Polygon',
//       coordinates,
//     },
//   }));

//   return {
//     type: 'FeatureCollection',
//     features,
//   };
// };

const DeliveryAreaForm = ({ isModalOpen, handleCancel, handleOk, area, isNewArea, carriers }) => {
  const mapRef = useRef();
  const drawControlRef = useRef();
  const [formArea] = Form.useForm();
  const [selectionType, setSelectionType] = useState(undefined);
  const [polygon, setPolygon] = useState({});
  const [postalCodes, setPostalCodes] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [drawControlKey, setDrawControlKey] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);

  const currentFeatures = useMemo(() => {
    const features = [];

    // Add existing area polygons only on initial load and if not yet initialized
    if (area?.type === 'AREA' && area?.area?.coordinates && !hasInitialized) {
      area.area.coordinates.forEach((coordinates, index) => {
        features.push({
          type: 'Feature',
          properties: { id: `existing_${index}` },
          geometry: {
            type: 'Polygon',
            coordinates,
          },
        });
      });
    }

    // Add current polygons from state
    Object.entries(polygon).forEach(([id, coordinates]) => {
      features.push({
        type: 'Feature',
        properties: { id },
        geometry: {
          type: 'Polygon',
          coordinates,
        },
      });
    });

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [polygon, area, hasInitialized]);

  // Initialize form and state when modal opens or area changes
  useEffect(() => {
    if (area) {
      formArea.setFieldsValue({
        name: area.name,
        carrierIntegrationId: area.carrierIntegrationId,
        description: area.description,
        cutoff: area.cutoff ? moment(area.cutoff, 'HH:mm') : null,
      });

      if (area.type === 'AREA') {
        setSelectionType('AREA');
        // Initialize polygon state with existing coordinates
        const initialPolygons = {};
        area?.area?.coordinates?.forEach((coordinates, index) => {
          initialPolygons[`existing_${index}`] = coordinates;
        });
        setPolygon(initialPolygons);
        setHasInitialized(true);
      } else if (area.type === 'POSTAL_CODES') {
        setSelectionType('POSTAL_CODES');
        setPostalCodes(area.postalCodes || []);
        setSelectedCountry(area.country);
        setHasInitialized(true);
      }
    } else {
      setSelectionType('AREA');
      setPolygon({});
      setHasInitialized(true);
      formArea.resetFields();
    }
  }, [formArea, isModalOpen, area]);

  // Update map bounds when polygons change
  useEffect(() => {
    if (mapRef.current && Object.keys(polygon).length > 0) {
      requestAnimationFrame(() => {
        setMapBounds();
      });
    }
  }, [polygon]);

  // Sync DrawControl with polygon state - more selective approach
  useEffect(() => {
    if (drawControlRef.current && selectionType === 'AREA') {
      const draw = drawControlRef.current.getControl();
      if (draw) {
        const currentDrawFeatures = draw.getAll().features;
        const currentDrawIds = new Set(currentDrawFeatures.map((f) => f.id));
        const stateIds = new Set(Object.keys(polygon));

        // Only update if there's actually a difference between draw state and polygon state
        const needsUpdate =
          currentDrawIds.size !== stateIds.size ||
          [...currentDrawIds].some((id) => !stateIds.has(id)) ||
          [...stateIds].some((id) => !currentDrawIds.has(id));

        if (needsUpdate) {
          // Clear only the features that should be removed
          const toDelete = currentDrawFeatures.filter((f) => !stateIds.has(f.id)).map((f) => f.id);
          if (toDelete.length > 0) {
            draw.delete(toDelete);
          }

          // Add only the features that are missing
          const toAdd = Object.entries(polygon).filter(([id]) => !currentDrawIds.has(id));
          if (toAdd.length > 0) {
            const featuresToAdd = {
              type: 'FeatureCollection',
              features: toAdd.map(([id, coordinates]) => ({
                type: 'Feature',
                properties: { id },
                geometry: {
                  type: 'Polygon',
                  coordinates,
                },
              })),
            };
            draw.add(featuresToAdd);
          }
        }
      }
    }
  }, [polygon, selectionType]);

  const [viewState, setViewState] = useState({
    longitude: 5.2913,
    latitude: 52.1326,
    zoom: 6,
  });

  const onFinishForm = (values) => {
    let params = {};
    if (selectionType === 'AREA') {
      params = {
        name: values.name,
        description: values.description,
        cutoff: values.cutoff ? values.cutoff.format('HH:mm') : null,
        area: polygon,
        type: 'AREA',
      };
    } else if (selectionType === 'POSTAL_CODES') {
      params = {
        name: values.name,
        description: values.description,
        cutoff: values.cutoff ? values.cutoff.format('HH:mm') : null,
        postalCodes: postalCodes,
        type: 'POSTAL_CODES',
        country: selectedCountry,
      };
    }

    // If an area exists, add the id so that it will be updated instead of creating new one
    if (area?.id) {
      params.id = area.id;
    }

    if (values.carrierIntegrationId) {
      params.carrierIntegrationId = values.carrierIntegrationId;
    }

    resetFormState();
    handleOk && handleOk(params);
  };

  const resetFormState = () => {
    // Clear draw control first if it exists
    if (drawControlRef.current) {
      const draw = drawControlRef.current.getControl();
      if (draw) {
        const allFeatures = draw.getAll().features;
        if (allFeatures.length > 0) {
          draw.delete(allFeatures.map((f) => f.id));
        }
      }
    }

    setPolygon({});
    setPostalCodes([]);
    setSelectionType(undefined);
    setSelectedCountry(null);
    setHasInitialized(false);
    formArea.resetFields();
    setDrawControlKey((prev) => prev + 1);
  };

  const onCancel = () => {
    resetFormState();
    handleCancel && handleCancel();
  };

  const onOk = () => {
    formArea.submit();
  };

  const setMapBounds = useCallback(() => {
    if (!mapRef.current) return;

    let bounds;

    // Check if there's valid polygon data
    if (polygon && Object.keys(polygon).length > 0) {
      let minLat = Infinity,
        maxLat = -Infinity,
        minLng = Infinity,
        maxLng = -Infinity;

      Object.values(polygon).forEach((coordinateArrays) => {
        coordinateArrays.forEach((ring) => {
          ring.forEach(([lng, lat]) => {
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          });
        });
      });

      // Only create bounds if we have valid coordinates
      if (minLat !== Infinity && maxLat !== -Infinity && minLng !== Infinity && maxLng !== -Infinity) {
        bounds = new mapboxgl.LngLatBounds(new mapboxgl.LngLat(minLng, minLat), new mapboxgl.LngLat(maxLng, maxLat));
      }
    }

    // Use default bounds if no valid polygon data
    if (!bounds) {
      bounds = new mapboxgl.LngLatBounds(
        new mapboxgl.LngLat(3.31497114423, 50.803721015),
        new mapboxgl.LngLat(7.09205325687, 53.5104033474),
      );
    }

    // Set map to bounds with animation
    mapRef.current.fitBounds(bounds, {
      padding: 60,
      duration: 1000,
      essential: true,
    });
  }, [polygon]);

  const onUpdate = useCallback((e) => {
    setPolygon((currPolygon) => {
      const newPolygon = { ...currPolygon };

      e.features.forEach((f) => {
        const id = f.properties?.id ?? f.id;
        if (f.geometry.type === 'Polygon') {
          newPolygon[id] = f.geometry.coordinates;
        }
      });

      return newPolygon;
    });
  }, []);

  const onDelete = useCallback((e) => {
    setPolygon((currPolygon) => {
      const newPolygon = { ...currPolygon };

      // Get all feature IDs that are being deleted
      const deletedIds = e.features.map((f) => f.properties?.id ?? f.id);

      // Remove the deleted features
      deletedIds.forEach((id) => {
        delete newPolygon[id];
      });

      return newPolygon;
    });
  }, []);

  const handleAreaTypeChange = (value) => {
    setSelectionType(value);
    if (value === 'AREA') {
      setPostalCodes([]);
      setSelectedCountry(null);
    } else {
      setPolygon({});
    }
  };

  const handleKMLUpload = async (file) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parser = new DOMParser();
          const kml = parser.parseFromString(e.target.result, 'text/xml');
          const geoJSON = toGeoJSON.kml(kml);

          setPolygon((currentPolygon) => {
            const newPolygon = { ...currentPolygon };
            const nextIndex = Object.keys(currentPolygon).length;

            geoJSON.features.forEach((feature, index) => {
              if (feature.geometry.type === 'Polygon') {
                newPolygon[`kml_${nextIndex + index}`] = feature.geometry.coordinates;
              }
            });

            return newPolygon;
          });
        } catch (error) {
          console.error('Error parsing KML file:', error);
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Error reading KML file:', error);
    }

    return false;
  };

  const clearAllPolygons = useCallback(() => {
    if (drawControlRef.current) {
      const draw = drawControlRef.current.getControl();
      if (draw) {
        // Clear all features from the draw control first
        const allFeatures = draw.getAll().features;
        if (allFeatures.length > 0) {
          draw.delete(allFeatures.map((f) => f.id));
        }
      }
    }
    // Then clear the state
    setPolygon({});
  }, []);

  const PolygonInfo = ({ polygon }) => {
    const polygonCount = Object.keys(polygon).length;

    return (
      <div
        className="polygon-info"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Tooltip
            title="To draw a polygon, click on the map and drag the mouse to create a shape. Double-click to finish the polygon. You can also upload a KML file to import existing polygons."
            placement="bottom"
          >
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>

          <span style={{ fontWeight: 500 }}>
            {polygonCount} {polygonCount === 1 ? 'polygon' : 'polygons'}
          </span>
        </div>
        {polygonCount > 0 && (
          <Button
            type="link"
            danger
            onClick={clearAllPolygons}
            icon={<DeleteOutlined />}
            style={{ padding: '4px 8px' }}
          >
            Clear all
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="DeliveryAreaForm">
      <Row gutter={16}>
        <Col span={6}>
          <Form
            form={formArea}
            scrollToFirstError
            requiredMark={false}
            className="FormContent"
            layout="vertical"
            onFinish={onFinishForm}
            initialValues={null}
          >
            <Row gutter={[16, 4]}>
              {isNewArea && (
                <Col span={24}>
                  <Form.Item label="How do you want to define the area?" name="areaType">
                    <Radio.Group
                      value={selectionType}
                      buttonStyle="solid"
                      size="large"
                      style={{ width: '100%' }}
                      onChange={(e) => handleAreaTypeChange(e.target.value)}
                    >
                      <Radio.Button value="AREA" style={{ width: '50.00%', textAlign: 'center' }}>
                        Draw Area
                      </Radio.Button>
                      <Radio.Button value="POSTAL_CODES" style={{ width: '50.00%', textAlign: 'center' }}>
                        Postal Codes
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}
              {selectionType === 'AREA' && (
                <>
                  <Col span={24}>
                    <Form.Item label="Upload KML File">
                      <Upload accept=".kml" beforeUpload={handleKMLUpload} showUploadList={false} multiple={true}>
                        <Button icon={<InboxOutlined />}>Upload KML(s)</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <PolygonInfo polygon={polygon} />
                  </Col>
                </>
              )}

              <Col span={24}>
                <Form.Item label="Delivery Area Name" name="name" rules={[{ required: true, message: 'Required' }]}>
                  <Input size="large" placeholder="Delivery Area Name" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Description" name="description">
                  <Input size="large" placeholder="Description..." />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Cutoff Time" name="cutoff" rules={[{ required: true, message: 'Required' }]}>
                  <TimePicker size="large" format="HH:mm" placeholder="Select cutoff time" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Carrier" name="carrierIntegrationId" rules={[{ required: false }]}>
                  <Select size="large" placeholder="Select a carrier" allowClear>
                    {carriers?.map((carrier) => (
                      <Select.Option key={carrier.id} value={carrier.id}>
                        {carrier.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
        <Col span={18}>
          {selectionType === 'AREA' && (
            <ReactMapGL
              ref={mapRef}
              {...viewState}
              width="100%"
              height="100%"
              mapStyle="mapbox://styles/maurice-sparqle/cll2ad40r00e801pd7cvn31eo"
              mapboxAccessToken={process.env.REACT_APP_MAP_BOX_KEY}
              onMove={(evt) => setViewState(evt.viewState)}
              onLoad={setMapBounds}
            >
              <DrawControl
                ref={drawControlRef}
                position="top-left"
                initialFeatures={currentFeatures}
                displayControlsDefault={false}
                controls={{
                  polygon: true,
                  trash: true,
                }}
                defaultMode="draw_polygon"
                onCreate={onUpdate}
                onUpdate={onUpdate}
                onDelete={onDelete}
                key={drawControlKey}
                styles={[
                  {
                    id: 'gl-draw-polygon-fill',
                    type: 'fill',
                    filter: ['all', ['==', '$type', 'Polygon']],
                    paint: {
                      'fill-color': '#3bb2d0',
                      'fill-opacity': 0.3,
                    },
                  },
                  {
                    id: 'gl-draw-polygon-stroke',
                    type: 'line',
                    filter: ['all', ['==', '$type', 'Polygon']],
                    paint: {
                      'line-color': '#3bb2d0',
                      'line-width': 2,
                    },
                  },
                ]}
              />
            </ReactMapGL>
          )}
          {selectionType === 'POSTAL_CODES' && (
            <PostalCodeInput
              className="PostalCodeInput"
              postalCodes={postalCodes}
              setPostalCodes={setPostalCodes}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
            />
          )}
        </Col>
      </Row>
      <div className="FormFooter">
        <Button className="ButtonCancel" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="CreateButton ButtonPrimary" type="primary" onClick={onOk}>
          {area ? 'Edit' : 'Create'}
          <ReactSVG className="icon" src="/icons/done.svg" />
        </Button>
      </div>
    </div>
  );
};

export default DeliveryAreaForm;
